import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { TeamKpiCards } from "@/components/pages/analytics/front-desk/components/team/TeamKpiCards";
import { TeamSegmentPills } from "@/components/pages/analytics/front-desk/components/team/TeamSegmentPills";
import {
  DEFAULT_TEAM_SEGMENT,
  parseTeamSegment,
} from "@/components/pages/analytics/front-desk/components/team/mockData";
import type { TeamSegment } from "@/components/pages/analytics/front-desk/components/team/types";
import { SERVICES_CONTENT_GAP } from "@/components/pages/analytics/front-desk/components/services/constants";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAdminTeamHotels } from "../hooks/useAdminTeamHotels";
import { useAdminTenantTableExport } from "../hooks/useAdminTenantTableExport";
import { AdminTeamHotelsTable } from "./team/AdminTeamHotelsTable";

export const AdminTeamTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamSegment = parseTeamSegment(searchParams.get("teamSegment"));
  const {
    tenantId,
    summary,
    kpiComparison,
    tableRows,
    isLoading,
    isError,
    isReady,
  } = useAdminTeamHotels(dateRange, teamSegment);

  useAdminTenantTableExport({
    tab: "teams",
    dateRange,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.team.selectDateRange",
          "Select a date range to view hotel team analytics.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.team.loadError", "Failed to load team data.")}
      </p>
    );
  }

  return (
    <section className="space-y-6 font-sans">
      <TeamSegmentPills
        value={teamSegment}
        onChange={(value: TeamSegment) => {
          setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            nextParams.set("tab", "team");
            nextParams.delete("staffId");
            nextParams.delete("teamId");
            if (value === DEFAULT_TEAM_SEGMENT) {
              nextParams.delete("teamSegment");
            } else {
              nextParams.set("teamSegment", value);
            }
            return nextParams;
          });
        }}
      />

      <div
        className={cn(
          "flex w-full max-w-full flex-col transition-opacity duration-150",
          isLoading && "opacity-70",
        )}
        style={{ gap: SERVICES_CONTENT_GAP }}
      >
        <TeamKpiCards
          summary={summary}
          segment={teamSegment}
          comparisonByKey={kpiComparison}
        />

        <AdminTeamHotelsTable
          rows={tableRows}
          loading={isLoading}
          context={{ teamSegment }}
        />
      </div>
    </section>
  );
};
